import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Alert,
  Collapse,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Typography,
  Button,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Star,
  StarBorder,
  DragIndicator,
  KeyboardArrowDown,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEntityTypes, useMenuConfig } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { useMenuPrefsStore } from '@/stores/useMenuPrefsStore';
import { useUserPreferences, useSaveUserPreferences } from '@/hooks/useUserPreferences';
import { LoadingSkeleton } from './shared/LoadingSkeleton';
import { buildMenu, MenuGroup, MenuLeaf, MenuItem } from '@/utils/navigation';
import { defaultIcons, resolveIcon } from '@/utils/icons';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';

type MenuProps = {
  collapsed?: boolean;
};

/** Max number of visible favorites before showing "more" */
const MAX_VISIBLE_FAVORITES = 3;

interface SortableFavoriteItemProps {
  id: string;
  leaf: { key: string; label: string; route: string; iconName?: string };
  onNavigate: (route: string) => void;
  onRemove: (key: string) => void;
}

const SortableFavoriteItem: React.FC<SortableFavoriteItemProps> = ({ id, leaf, onNavigate, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem ref={setNodeRef} style={style} disablePadding>
      <ListItemButton onClick={() => onNavigate(leaf.route)} sx={{ py: 0.25 }}>
        <IconButton size="small" sx={{ cursor: 'grab', mr: 0.5, p: '2px' }} {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
          <DragIndicator fontSize="small" sx={{ color: 'text.disabled' }} />
        </IconButton>
        <ListItemIcon sx={{ minWidth: 36 }}>
          {resolveIcon(leaf.iconName, defaultIcons.leaf)}
        </ListItemIcon>
        <ListItemText primary={leaf.label} />
        <IconButton
          edge="end"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(leaf.key);
          }}
        >
          <Star color="warning" fontSize="small" />
        </IconButton>
      </ListItemButton>
    </ListItem>
  );
};

const normalizePath = (path: string | undefined) => {
  if (!path) return '/';
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  if (withSlash === '/') return withSlash;
  return withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
};

// Keep route comparisons scoped to full segments so /location doesn't highlight /location_type.
const isRouteActive = (pathname: string, route: string) => {
  const [routePath] = route.split('?');
  const normalizedRoute = normalizePath(routePath);
  const normalizedCurrent = normalizePath(pathname);
  if (normalizedRoute === '/') {
    return normalizedCurrent === '/';
  }
  return (
    normalizedCurrent === normalizedRoute || normalizedCurrent.startsWith(`${normalizedRoute}/`)
  );
};

export const Menu: React.FC<MenuProps> = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const frontWindowLabel = iemConfig.features.front_window.menu_label || 'Front Window';
  const ieKitMenuLabel = iemConfig.features.ie_kits.menu_label;
  const { data: entityTypes, isLoading, error } = useEntityTypes();
  const { data: uiMenu, isLoading: menuLoading, error: menuError } = useMenuConfig();
  const { isAuthenticated, hasAnyRole, isAdmin, isFrontWindowOnly, isViewer } = useAuth();
  /** Whether to show the full sidebar navigation (dashboard, taxonomy groups, etc.). */
  const showFullNav = !isFrontWindowOnly;
  const openGroups = useMenuPrefsStore((s) => s.openGroups);
  const setGroupOpen = useMenuPrefsStore((s) => s.setGroupOpen);
  const toggleGroupOpen = useMenuPrefsStore((s) => s.toggleGroup);
  const [hoverMenuOpen, setHoverMenuOpen] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [favoritesExpanded, setFavoritesExpanded] = useState(false);
  const favoritesRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const favorites = useMenuPrefsStore((s) => s.favorites);
  const toggleFavorite = useMenuPrefsStore((s) => s.toggleFavorite);
  const setFavorites = useMenuPrefsStore((s) => s.setFavorites);
  const expandAllStore = useMenuPrefsStore((s) => s.expandAll);
  const collapseAllStore = useMenuPrefsStore((s) => s.collapseAll);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Persist favorites to user profile in database
  const { data: userPrefs } = useUserPreferences();
  const { mutate: savePrefs } = useSaveUserPreferences();
  const prefsLoadedRef = useRef(false);

  // Load favorites from DB on first load
  useEffect(() => {
    if (userPrefs?.menu_favorites && !prefsLoadedRef.current) {
      prefsLoadedRef.current = true;
      setFavorites(userPrefs.menu_favorites);
    }
  }, [userPrefs, setFavorites]);

  // Save favorites to DB whenever they change (after initial load)
  const prevFavoritesRef = useRef<string[]>(favorites);
  useEffect(() => {
    if (!prefsLoadedRef.current) return;
    if (JSON.stringify(prevFavoritesRef.current) === JSON.stringify(favorites)) return;
    prevFavoritesRef.current = favorites;
    savePrefs({ menu_favorites: favorites });
  }, [favorites, savePrefs]);

  // Build items consistently so hooks remain unconditional
  const items = useMemo(
    () => {
      const built = entityTypes ? buildMenu(entityTypes, uiMenu ?? null) : [];
      // Apply ie_kits menu_label override from module.iem config
      if (ieKitMenuLabel) {
        for (const item of built) {
          if ((item as MenuGroup).kind === 'group') {
            const g = item as MenuGroup;
            const leaf = g.children.find((c) => c.key === 'ie-kit-management');
            if (leaf) { leaf.label = ieKitMenuLabel; break; }
          }
        }
      }
      return built;
    },
    [entityTypes, uiMenu, ieKitMenuLabel],
  );

  // Filter out admin-only items for non-admin users
  const visibleItems = useMemo(
    () =>
      items
        .map((item) => {
          if ((item as MenuGroup).kind === 'group') {
            const g = item as MenuGroup;
            const children = (isAdmin ? g.children : g.children.filter((leaf) => !leaf.adminOnly))
              .filter((leaf) => !(leaf.viewerHidden && isViewer));
            if (children.length === 0) return null;
            return { ...g, children };
          }
          const leaf = item as MenuLeaf;
          if (leaf.adminOnly && !isAdmin) return null;
          if (leaf.viewerHidden && isViewer) return null;
          return leaf;
        })
        .filter(Boolean) as MenuItem[],
    [items, isAdmin, isViewer],
  );

  // Flatten leaves for search and favorites (computed before any early returns)
  const flattenLeaves = useMemo(() => {
    const leaves: Array<{
      key: string;
      label: string;
      route: string;
      iconName?: string;
      group?: string;
    }> = [];
    visibleItems.forEach((it) => {
      if ((it as MenuLeaf).kind === 'leaf') {
        const leaf = it as MenuLeaf;
        leaves.push({
          key: leaf.key,
          label: leaf.label,
          route: leaf.route,
          iconName: leaf.iconName,
        });
      } else {
        const g = it as MenuGroup;
        g.children.forEach((leaf) =>
          leaves.push({
            key: leaf.key,
            label: leaf.label,
            route: leaf.route,
            iconName: leaf.iconName,
            group: g.label,
          }),
        );
      }
    });
    return leaves;
  }, [items]);

  const favoriteLeaves = useMemo(() => {
    const byKey = new Map(flattenLeaves.map((l) => [l.key, l] as const));
    return favorites.map((k) => byKey.get(k)).filter(Boolean) as typeof flattenLeaves;
  }, [favorites, flattenLeaves]);

  // Compute whether all groups are expanded or collapsed (must be before early returns)
  const groupKeys = useMemo(
    () => visibleItems.filter((i) => (i as MenuGroup).kind === 'group').map((g) => (g as MenuGroup).key),
    [visibleItems],
  );
  const allExpanded = useMemo(
    () => groupKeys.length > 0 && groupKeys.every((k) => openGroups[k]),
    [groupKeys, openGroups],
  );
  const allCollapsed = useMemo(
    () => groupKeys.length > 0 && groupKeys.every((k) => !openGroups[k]),
    [groupKeys, openGroups],
  );

  // Ensure group with active child is open (merged into store)
  useEffect(() => {
    visibleItems.forEach((it) => {
      if ((it as MenuGroup).kind === 'group') {
        const g = it as MenuGroup;
        const anyActive = g.children.some((c) => isRouteActive(location.pathname, c.route));
        if (anyActive && !openGroups[g.key]) setGroupOpen(g.key, true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleItems, location.pathname, setGroupOpen]);

  // Collapse expanded favorites when clicking outside the favorites section
  useEffect(() => {
    if (!favoritesExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (favoritesRef.current && !favoritesRef.current.contains(e.target as Node)) {
        setFavoritesExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [favoritesExpanded]);

  if (isLoading || menuLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <LoadingSkeleton variant="list" count={8} />
      </Box>
    );
  }

  if (error || menuError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load navigation</Alert>
      </Box>
    );
  }

  const isSelected = (path: string) => location.pathname === path;
  const toggleGroup = (key: string) => toggleGroupOpen(key);

  const expandAll = () => {
    expandAllStore(groupKeys);
  };
  const collapseAll = () => {
    collapseAllStore(groupKeys);
  };
  const toggleAll = () => {
    if (allExpanded) {
      collapseAll();
    } else {
      expandAll();
    }
  };

  const handleHoverMenuOpen = (event: React.MouseEvent<HTMLElement>, groupKey: string) => {
    if (collapsed) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setAnchorEl(event.currentTarget);
      setHoverMenuOpen(groupKey);
    }
  };

  const handleHoverMenuClose = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverMenuOpen(null);
      setAnchorEl(null);
    }, 150);
  };

  const handleHoverMenuEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = favorites.indexOf(active.id as string);
      const newIndex = favorites.indexOf(over.id as string);
      setFavorites(arrayMove(favorites, oldIndex, newIndex));
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Main Navigation - only show if authenticated */}
      {isAuthenticated && (
        <>
          {/* Favorites */}
          {showFullNav && !collapsed && favoriteLeaves.length > 0 && (
            <Box ref={favoritesRef} sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 2, pt: 1, pb: 0, lineHeight: 1.5, flexShrink: 0 }}>
                Favorites
              </Typography>
              <List dense sx={{ px: 1, pb: 0, pt: 0 }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={favorites} strategy={verticalListSortingStrategy}>
                    {(favoritesExpanded ? favoriteLeaves : favoriteLeaves.slice(0, MAX_VISIBLE_FAVORITES)).map((leaf) => (
                      <SortableFavoriteItem
                        key={leaf.key}
                        id={leaf.key}
                        leaf={leaf}
                        onNavigate={(route) => {
                          setFavoritesExpanded(false);
                          navigate(route);
                        }}
                        onRemove={toggleFavorite}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </List>
              {favoriteLeaves.length > MAX_VISIBLE_FAVORITES && (
                <Box
                  onClick={() => setFavoritesExpanded(!favoritesExpanded)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.secondary', transform: favoritesExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    {favoritesExpanded ? 'Show less' : `${favoriteLeaves.length - MAX_VISIBLE_FAVORITES} more`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Static navigation items */}
          <List>
            {showFullNav && (
              <ListItem disablePadding>
                <Tooltip
                  title={collapsed ? 'Dashboard' : ''}
                  placement="right"
                  disableHoverListener={!collapsed}
                  enterDelay={100}
                >
                  <ListItemButton
                    selected={isSelected('/')}
                    onClick={() => navigate('/')}
                    sx={{
                      justifyContent: collapsed ? 'center' : 'initial',
                      px: collapsed ? 2 : 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>
                      {defaultIcons.dashboard}
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary="Dashboard" />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}

            {!isViewer && hasAnyRole(['user', 'admin', 'frontwindow']) && (
              <ListItem disablePadding>
                <Tooltip
                  title={collapsed ? frontWindowLabel : ''}
                  placement="right"
                  disableHoverListener={!collapsed}
                  enterDelay={100}
                >
                  <ListItemButton
                    selected={isSelected('/customer-service')}
                    onClick={() => navigate('/customer-service')}
                    sx={{ justifyContent: collapsed ? 'center' : 'initial', px: collapsed ? 2 : 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>
                      {resolveIcon('storefront-outline', defaultIcons.leaf)}
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary={frontWindowLabel} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}

            {showFullNav && (
              <ListItem disablePadding>
                <Tooltip
                  title={collapsed ? 'Transactions' : ''}
                  placement="right"
                  disableHoverListener={!collapsed}
                  enterDelay={100}
                >
                  <ListItemButton
                    selected={isSelected('/transactions')}
                    onClick={() => navigate('/transactions')}
                    sx={{ justifyContent: collapsed ? 'center' : 'initial', px: collapsed ? 2 : 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>
                      {resolveIcon('History', defaultIcons.leaf)}
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary="Transactions" />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}

            {showFullNav && (
              <ListItem disablePadding>
                <Tooltip
                  title={collapsed ? 'Reports' : ''}
                  placement="right"
                  disableHoverListener={!collapsed}
                  enterDelay={100}
                >
                  <ListItemButton
                    selected={isSelected('/reports')}
                    onClick={() => navigate('/reports')}
                    sx={{ justifyContent: collapsed ? 'center' : 'initial', px: collapsed ? 2 : 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>
                      {resolveIcon('Assessment', defaultIcons.leaf)}
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary="Reports" />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 0 }} />

          {showFullNav && !collapsed && groupKeys.length > 0 && (
            <Box sx={{ px: 2, py: 0.5 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                onClick={toggleAll}
                startIcon={allExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {allExpanded ? 'Collapse All' : allCollapsed ? 'Expand All' : 'Expand All'}
              </Button>
            </Box>
          )}

          {/* Dynamic taxonomy-driven navigation */}
          {showFullNav && (
            <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
              <List>
                {visibleItems.map((item) => {
              if ((item as MenuLeaf).kind === 'leaf') {
                const leaf = item as MenuLeaf;
                const selected = isRouteActive(location.pathname, leaf.route);
                return (
                  <ListItem key={leaf.key} disablePadding>
                    <Tooltip
                      title={collapsed ? leaf.label : ''}
                      placement="right"
                      disableHoverListener={!collapsed}
                      enterDelay={100}
                    >
                      <ListItemButton
                        selected={selected}
                        onClick={() => navigate(leaf.route)}
                        sx={{
                          justifyContent: collapsed ? 'center' : 'initial',
                          pl: collapsed ? 2 : 2,
                          pr: collapsed ? 2 : 4,
                          position: 'relative',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>
                          {resolveIcon(leaf.iconName, defaultIcons.leaf)}
                        </ListItemIcon>
                        {!collapsed && <ListItemText primary={leaf.label} />}
                        {!collapsed && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(leaf.key);
                            }}
                            sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', opacity: favorites.includes(leaf.key) ? 1 : 0, '&:hover': { opacity: 1 }, '.MuiListItemButton-root:hover &': { opacity: 1 }, p: '4px' }}
                          >
                            {favorites.includes(leaf.key) ? <Star color="warning" fontSize="small" /> : <StarBorder fontSize="small" />}
                          </IconButton>
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              }
              const group = item as MenuGroup;
              const open = !!openGroups[group.key];
              return (
                <Box key={group.key}>
                  <ListItem disablePadding>
                    <Tooltip
                      title={collapsed ? group.label : ''}
                      placement="right"
                      disableHoverListener={!collapsed}
                      enterDelay={100}
                    >
                      <ListItemButton
                        onClick={() => !collapsed && toggleGroup(group.key)}
                        onMouseEnter={(e) => handleHoverMenuOpen(e, group.key)}
                        onMouseLeave={() => collapsed && handleHoverMenuClose()}
                        sx={{
                          justifyContent: collapsed ? 'center' : 'initial',
                          px: collapsed ? 2 : 2,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36 }}>
                          {resolveIcon(group.iconName, defaultIcons.group)}
                        </ListItemIcon>
                        {!collapsed && <ListItemText primary={group.label} />}
                        {!collapsed && (open ? <ExpandLess /> : <ExpandMore />)}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                  <Collapse in={!collapsed && open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {group.children.map((leaf) => {
                        const selected = isRouteActive(location.pathname, leaf.route);
                        return (
                          <ListItem key={leaf.key} disablePadding sx={{ pl: collapsed ? 0 : 4 }}>
                            <Tooltip
                              title={collapsed ? leaf.label : ''}
                              placement="right"
                              disableHoverListener={!collapsed}
                              enterDelay={100}
                            >
                              <ListItemButton
                                selected={selected}
                                onClick={() => navigate(leaf.route)}
                                sx={{
                                  justifyContent: collapsed ? 'center' : 'initial',
                                  pl: collapsed ? 2 : 2,
                                  pr: collapsed ? 2 : 4,
                                  position: 'relative',
                                }}
                              >
                                <ListItemIcon
                                  sx={{ minWidth: collapsed ? 0 : 36 }}
                                >
                                  {resolveIcon(leaf.iconName, defaultIcons.leaf)}
                                </ListItemIcon>
                                {!collapsed && <ListItemText primary={leaf.label} />}
                                {!collapsed && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(leaf.key);
                                    }}
                                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', opacity: favorites.includes(leaf.key) ? 1 : 0, '&:hover': { opacity: 1 }, '.MuiListItemButton-root:hover &': { opacity: 1 }, p: '4px' }}
                                  >
                                    {favorites.includes(leaf.key) ? <Star color="warning" fontSize="small" /> : <StarBorder fontSize="small" />}
                                  </IconButton>
                                )}
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
                })}
              </List>
            </Box>
          )}
        </>
      )}

      {/* Hover Menu for collapsed groups */}
      {collapsed && hoverMenuOpen && anchorEl && (
        <Popper
          open={Boolean(hoverMenuOpen)}
          anchorEl={anchorEl}
          placement="right-start"
          sx={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleHoverMenuClose}>
            <Paper
              elevation={8}
              onMouseEnter={handleHoverMenuEnter}
              onMouseLeave={handleHoverMenuClose}
              sx={{
                minWidth: 200,
                maxWidth: 300,
                ml: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <List dense>
                {visibleItems
                  .filter(
                    (item) => (item as MenuGroup).kind === 'group' && item.key === hoverMenuOpen,
                  )
                  .map((item) => {
                    const group = item as MenuGroup;
                    return group.children.map((leaf: MenuLeaf) => {
                      const selected = isRouteActive(location.pathname, leaf.route);
                      return (
                        <ListItem key={leaf.key} disablePadding>
                          <ListItemButton
                            selected={selected}
                            onClick={() => {
                              navigate(leaf.route);
                              handleHoverMenuClose();
                            }}
                            sx={{ px: 2, py: 1 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {resolveIcon(leaf.iconName, defaultIcons.leaf)}
                            </ListItemIcon>
                            <ListItemText primary={leaf.label} />
                          </ListItemButton>
                        </ListItem>
                      );
                    });
                  })}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};
